import { Box } from "@chakra-ui/react";
import { ColorIcon, customEvent, NextLink, Spacing } from "goi_common";
import { BaseStack, baseColors, BaseButton } from "goi_common/src/design-system";

import { ParlorDetailProps } from "@/types/parlor";

/**
 * 지역 장례식장 대표 리스팅 (h1 바로 아래).
 * 사진+이름+유형·빈소 정보 rows, 하단 전체보기로 아래 목록 섹션 스크롤.
 */
interface RegionParlorListPreviewProps {
  parlors: ParlorDetailProps[];
  /** 전체보기 클릭 시 스크롤할 목록 섹션 id */
  listTargetId: string;
}

const MAX_PREVIEW_ITEMS = 5;

const MANAGED_TYPE_LABEL: Record<string, string> = {
  전문: "전문장례식장",
  대학병원: "대학병원",
  공설: "공설",
};

function RegionParlorListPreview({ parlors, listTargetId }: RegionParlorListPreviewProps) {
  const items = parlors.slice(0, MAX_PREVIEW_ITEMS);

  if (items.length === 0) return null;

  return (
    <BaseStack width="100%" px={24}>
      <BaseStack as="ol" width="100%" spacing={12} css={{ listStyle: "none", paddingLeft: 0 }}>
        {items.map((parlor) => {
          const imageUrl = parlor?.images?.[0]?.url;
          const managedLabel = MANAGED_TYPE_LABEL[parlor?.managed_type] ?? parlor?.managed_type;
          const binsoCount = parlor?.binsos?.length ?? 0;
          const binsoAvg = parlor?.binso_average_charge ?? 0;

          return (
            <Box as="li" key={parlor.id} width="100%">
              <NextLink
                href={`/parlor/${parlor.id}/`}
                event={{
                  newGtm: {
                    path: "parlor_region",
                    trigger_and_target: "parlor_item-uv_parlor_detail",
                  },
                }}
              >
                <Box display="flex" alignItems="center" width="100%" cursor="pointer">
                  {imageUrl ? (
                    <img
                      src={imageUrl}
                      alt={`${parlor.companyname} 사진`}
                      loading="lazy"
                      width={96}
                      height={72}
                      style={{
                        width: "96px",
                        height: "72px",
                        borderRadius: "10px",
                        objectFit: "cover",
                        flexShrink: 0,
                        background: baseColors.fill_normal,
                      }}
                    />
                  ) : (
                    <Box
                      width="96px"
                      height="72px"
                      borderRadius="10px"
                      flexShrink={0}
                      background={baseColors.fill_normal}
                    />
                  )}
                  <Box width="12px" flexShrink={0} />
                  <Box minWidth={0} flex={1}>
                    <Box
                      className="body_2_normal_sb"
                      color={baseColors.label_normal}
                      overflow="hidden"
                      textOverflow="ellipsis"
                      whiteSpace="nowrap"
                    >
                      {parlor.companyname}
                    </Box>
                    <Box height="4px" />
                    <Box className="caption_1_r" color={baseColors.label_assistive_2}>
                      {managedLabel ? `${managedLabel}` : ""}
                      {binsoCount > 0 ? ` · 빈소 ${binsoCount}개` : ""}
                      {typeof binsoAvg === "number" && binsoAvg > 0
                        ? ` · 빈소료 평균 ${binsoAvg.toLocaleString()}원`
                        : ""}
                    </Box>
                  </Box>
                  <Box width="8px" flexShrink={0} />
                  <ColorIcon name="arrow_right_small" size={16} color={baseColors.label_muted_2} />
                </Box>
              </NextLink>
            </Box>
          );
        })}
      </BaseStack>
      {parlors.length > items.length && (
        <>
          <Spacing gap={16} />
          <BaseButton
            type="assistive_1"
            size="medium"
            outlined
            onClick={() => {
              customEvent({
                newGtm: { path: "parlor_region", trigger_and_target: "list_more-scroll_list" },
              });
              document
                .getElementById(listTargetId)
                ?.scrollIntoView({ behavior: "smooth", block: "start" });
            }}
          >
            <Box className="body_2_normal_sb">장례식장 전체 보기 ({parlors.length}곳)</Box>
          </BaseButton>
        </>
      )}
    </BaseStack>
  );
}

export default RegionParlorListPreview;
